import { Drawer, InfoCard, Input, P, Tag } from '@dnb/eufemia';
import { Button } from '@dnb/eufemia';
import { filter } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import type { ApiDto } from '@portals/shared/portal/ApiDto';
import { type JSX, PropsWithChildren, useMemo, useState } from 'react';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import HeroTopContent from '@/components/HeroPage/HeroTopContent';
import useAuth from '@/hooks/useAuth';
import { useBackend } from '@/hooks/useBackend';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { useUser } from '@/hooks/useUser';
import { SERVICE_DOMAIN_KEY } from '@/pages/explorer/filters/ServiceDomainFilter';
import { STAGE_FILTER_KEY } from '@/pages/explorer/filters/StageFilter';
import { TAG_FILTER_KEY } from '@/pages/explorer/filters/TagFilter';
import { capitalizeFirstLetterOfString } from '@/utils/textUtils';

import { API_EXPLORER_TABS } from '../../content';
import ApiCard from '../ApiCard';
import CommonRepoApiInfoCard from '../CommonRepoApiInfoCard';
import { ApiFilters } from './ApiFilters';
import { useApiFilters } from './useApiFilters';

type ApiExplorerProps = PropsWithChildren<{
  title: string;
  apiType?: API_EXPLORER_TABS;
  apis: ApiDto[] | undefined;
  onApiFavouriteChange: (apiId: string, isFavorite: boolean) => void;
}>;

const StyledDrawer = styled(Drawer)`
  background-color: var(--color-black-3);
`;

export default function ApiPage({
  title,
  apiType = API_EXPLORER_TABS.Commercial,
  apis,
  onApiFavouriteChange,
  children,
}: ApiExplorerProps): JSX.Element {
  const { featureFlags } = useFeatureFlags();
  const { updateFilterInURL, clearAllFilters } = useUrlFilters();
  const { isLoggedIn } = useUser();
  const { isInternal } = useAuth();
  const { data: serviceDomains } = useBackend('/service-now/service-domains');

  const isDnbUser = isInternal && isLoggedIn;

  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);

  const {
    query,
    setQuery,
    tagFilter,
    filteredApis,
    tagOptions,
    stageFilter,
    serviceDomainFilter,
    serviceDomainOptions,
  } = useApiFilters(apis, serviceDomains);

  const searchField = useMemo(() => {
    return (
      <Input clear icon="loupe" on_change={({ value }) => setQuery(value)} placeholder="Search" stretch value={query} />
    );
  }, [query, setQuery]);

  const filters = <ApiFilters apiType={apiType} apis={apis} isDnbUser={isDnbUser} serviceDomains={serviceDomains} />;

  return (
    <HeroPage banner="explorer" skeleton={!apis} title={title}>
      {children && <HeroTopContent>{children}</HeroTopContent>}
      <HeroMenu>
        {searchField}
        <Button
          onClick={() => {
            clearAllFilters();
            setQuery('');
          }}
          space={{ top: 'small' }}
          variant="tertiary"
        >
          Clear Filters
        </Button>
        {filters}
      </HeroMenu>
      <HeroContent>
        <HeroContentMenu>
          {searchField}
          <StyledDrawer
            id="tagFilterDrawer"
            onClose={() => setFilterMenuOpen(false)}
            openState={filterMenuOpen}
            title="Filters"
            top="small"
            triggerAttributes={{
              icon: filter,
              text: 'Filters',
              variant: 'tertiary',
            }}
          >
            {filters}
          </StyledDrawer>
          {(tagFilter.length > 0 || serviceDomainFilter.length > 0) && (
            <div>
              <Tag.Group label="Filters" top="x-small">
                {tagFilter.length > 0 &&
                  tagFilter.map((tag) => (
                    <Tag
                      key={tag}
                      onClick={() =>
                        updateFilterInURL(
                          TAG_FILTER_KEY,
                          tagFilter,
                          tagOptions.filter((t) => t.selectedKey !== tag),
                        )
                      }
                      variant="removable"
                    >
                      {tag}
                    </Tag>
                  ))}
                {stageFilter.length > 0 &&
                  stageFilter.map((stage) => (
                    <Tag
                      key={stage}
                      onClick={() =>
                        updateFilterInURL(
                          STAGE_FILTER_KEY,
                          stageFilter.filter((s) => s !== stage),
                          stageFilter
                            .map((sf) => ({ selectedKey: sf, content: [sf] }))
                            .filter((sfOption) => sfOption.selectedKey !== stage),
                        )
                      }
                      variant="removable"
                    >
                      {capitalizeFirstLetterOfString(stage)}
                    </Tag>
                  ))}
                {serviceDomainFilter.length > 0 &&
                  serviceDomainFilter.map((serviceDomain) => (
                    <Tag
                      key={serviceDomain}
                      onClick={() =>
                        updateFilterInURL(
                          SERVICE_DOMAIN_KEY,
                          serviceDomainFilter,
                          serviceDomainOptions.filter((sdo) => sdo.selectedKey !== serviceDomain),
                        )
                      }
                      variant="removable"
                    >
                      {serviceDomainOptions.find((sdo) => sdo.selectedKey === serviceDomain)?.content}
                    </Tag>
                  ))}
              </Tag.Group>
            </div>
          )}
        </HeroContentMenu>
        <P modifier="medium" space={{ bottom: 'medium' }}>
          Showing {filteredApis.length} of {apis?.length} apis
        </P>
        {filteredApis.length > 0 ? (
          <div>
            {filteredApis.map((api) => (
              <ApiCard
                api={api}
                key={api.id}
                onApiFavouriteChange={(apiId, isFavorite) => onApiFavouriteChange(apiId, isFavorite)}
              />
            ))}
          </div>
        ) : featureFlags.ENABLE_COMMON_REPO_API ? (
          <CommonRepoApiInfoCard />
        ) : (
          <InfoCard text="No APIs match the selected filters." />
        )}
      </HeroContent>
    </HeroPage>
  );
}
